import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const SkillsInventory = ({ onNext, onSave, savedData = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkills, setSelectedSkills] = useState(savedData?.skills || []);
  const [customSkill, setCustomSkill] = useState('');

  const predefinedSkills = [
    // Technical Skills
    { category: 'Programming', skills: ['JavaScript', 'Python', 'Java', 'C++', 'React', 'Node.js', 'SQL', 'HTML/CSS'] },
    { category: 'Design', skills: ['UI/UX Design', 'Graphic Design', 'Adobe Creative Suite', 'Figma', 'Sketch', 'Prototyping'] },
    { category: 'Marketing', skills: ['Digital Marketing', 'SEO/SEM', 'Content Marketing', 'Social Media', 'Email Marketing', 'Analytics'] },
    { category: 'Business', skills: ['Project Management', 'Business Analysis', 'Strategic Planning', 'Financial Analysis', 'Sales'] },
    { category: 'Soft Skills', skills: ['Leadership', 'Communication', 'Problem Solving', 'Teamwork', 'Time Management', 'Adaptability'] }
  ];

  const allSkills = predefinedSkills?.flatMap(category => 
    category?.skills?.map(skill => ({ name: skill, category: category?.category }))
  );

  const filteredSkills = allSkills?.filter(skill =>
    skill?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) &&
    !selectedSkills?.some(selected => selected?.name === skill?.name)
  );

  const addSkill = (skillName, category = 'Custom') => {
    if (!selectedSkills?.some(skill => skill?.name === skillName)) {
      setSelectedSkills([...selectedSkills, { name: skillName, category, proficiency: 3 }]);
    }
  };

  const removeSkill = (skillName) => {
    setSelectedSkills(selectedSkills?.filter(skill => skill?.name !== skillName));
  };

  const updateProficiency = (skillName, proficiency) => {
    setSelectedSkills(selectedSkills?.map(skill =>
      skill?.name === skillName ? { ...skill, proficiency } : skill
    ));
  };

  const addCustomSkill = () => {
    if (customSkill?.trim()) {
      addSkill(customSkill?.trim(), 'Custom');
      setCustomSkill('');
    }
  };

  const handleNext = () => {
    const data = { skills: selectedSkills };
    onSave(data);
    onNext();
  };

  const getProficiencyLabel = (level) => {
    const labels = {
      1: 'Beginner',
      2: 'Basic',
      3: 'Intermediate',
      4: 'Advanced',
      5: 'Expert'
    };
    return labels?.[level];
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-text-primary mb-2">Skills Inventory</h3>
        <p className="text-text-secondary">
          Select your skills and rate your proficiency level. This helps us match you with relevant opportunities.
        </p>
      </div>
      {/* Search and Add Skills */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search skills (e.g., JavaScript, Marketing, Leadership)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Add custom skill"
              value={customSkill}
              onChange={(e) => setCustomSkill(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && addCustomSkill()}
              className="w-full sm:w-48"
            />
            <Button
              variant="outline"
              onClick={addCustomSkill}
              disabled={!customSkill?.trim()}
              iconName="Plus"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Skill Suggestions */}
        {searchTerm && filteredSkills?.length > 0 && (
          <div className="bg-muted rounded-md p-4 mb-4">
            <p className="text-sm font-medium text-text-primary mb-2">Suggestions:</p>
            <div className="flex flex-wrap gap-2">
              {filteredSkills?.slice(0, 8)?.map((skill) => (
                <button
                  key={skill?.name}
                  onClick={() => addSkill(skill?.name, skill?.category)}
                  className="px-3 py-1 bg-background border border-border rounded-full text-sm text-text-primary hover:bg-primary hover:text-primary-foreground spring-transition"
                >
                  {skill?.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Selected Skills */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-text-primary mb-4">
          Your Skills ({selectedSkills?.length})
        </h4>
        
        {selectedSkills?.length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="Target" size={48} className="mx-auto mb-4 opacity-50" />
            <p>No skills selected yet. Start by searching or adding your skills above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {predefinedSkills?.map((category) => {
              const categorySkills = selectedSkills?.filter(skill => skill?.category === category?.category);
              if (categorySkills?.length === 0) return null;

              return (
                <div key={category?.category} className="border border-border rounded-lg p-4">
                  <h5 className="font-medium text-text-primary mb-3">{category?.category}</h5>
                  <div className="space-y-3">
                    {categorySkills?.map((skill) => (
                      <div key={skill?.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-text-primary">{skill?.name}</span>
                          <button
                            onClick={() => removeSkill(skill?.name)}
                            className="text-destructive hover:text-destructive/80 spring-transition"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-text-secondary min-w-16">
                            {getProficiencyLabel(skill?.proficiency)}
                          </span>
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5]?.map((level) => (
                              <button
                                key={level}
                                onClick={() => updateProficiency(skill?.name, level)}
                                className={`w-6 h-6 rounded-full border-2 spring-transition ${
                                  level <= skill?.proficiency
                                    ? 'bg-primary border-primary' :'border-border hover:border-primary/50'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Custom Skills */}
            {selectedSkills?.some(skill => skill?.category === 'Custom') && (
              <div className="border border-border rounded-lg p-4">
                <h5 className="font-medium text-text-primary mb-3">Custom Skills</h5>
                <div className="space-y-3">
                  {selectedSkills?.filter(skill => skill?.category === 'Custom')?.map((skill) => (
                    <div key={skill?.name} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-text-primary">{skill?.name}</span>
                        <button
                          onClick={() => removeSkill(skill?.name)}
                          className="text-destructive hover:text-destructive/80 spring-transition"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-text-secondary min-w-16">
                          {getProficiencyLabel(skill?.proficiency)}
                        </span>
                        <div className="flex items-center space-x-1">
                          {[1, 2, 3, 4, 5]?.map((level) => (
                            <button
                              key={level}
                              onClick={() => updateProficiency(skill?.name, level)}
                              className={`w-6 h-6 rounded-full border-2 spring-transition ${
                                level <= skill?.proficiency
                                  ? 'bg-primary border-primary' :'border-border hover:border-primary/50'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button variant="outline" disabled>
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={selectedSkills?.length === 0}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Goals
        </Button>
      </div>
    </div>
  );
};

export default SkillsInventory;